import React, { useEffect, useRef } from "react";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import { Formik, Form, Field } from "formik";
import { PostMakeModel, UpdateMakeModel, GetFuelType } from "../../../Service/_index";
import CloseIcon from "@mui/icons-material/Close";
import Button from "@mui/material/Button";
import { ToastError } from "../../../UiComponents/Toaster/Toast";
import { Checkbox, FormControlLabel, Switch, Slider, Box } from "@mui/material";
import Styles from "../../../UiComponents/Scss/AddPopup.module.scss";
import { PROVIDER_COMPANIES } from "../../../Shared/CommonConstant";
import {
  upperCaseChange,
  digitsOnlyChange,
} from "../../Policy/OnlinePolicy/utils/inputHelpers";

// The IDV range is derived from the Manufacturer's Selling Price by the SAVED
// formula (backend collection "formula", document IDV_RANGE), not by numbers
// hardcoded here — this file used to keep its own 90%/99% constants alongside a
// second copy in idvHelpers.js. Edited from the IDV Calculator on the Make &
// Model list; until it loads, deriveIdvRange falls back to the same 90/99.
import {
  deriveIdvRange,
  calculateIdv,
  setActiveIdvFormula,
  getActiveIdvFormula,
  describeIdvSide,
  describeDepreciation,
} from "../../Policy/OnlinePolicy/utils/idvHelpers";
import { GetFormula } from "../../../Service/_index";
import { FORMULA_NAMES } from "../../../Service/SearchPolicy";

/** The "Not able to Find the required Model & Variant" flag is National-only.
 *
 *  Only national.js drives a portal through that option (it ticks
 *  isNonGICSelected and fills the selling price instead of the IDV).
 *  relianceForm.js has no such handling, and the Online Policy form sets
 *  `idv: 0` whenever the flag is on — so offering it for Reliance or KSHEMA
 *  would submit a zero-IDV policy. */
const isNationalCompany = (company) =>
  String(company || "").trim().toLowerCase() === "national";

/** Who is asked for a Vehicle Variant — and required to give one.
 *
 *  Deliberately NOT tied to isNationalCompany: the variant and the "model not
 *  found" flag used to share that one predicate, but they answer different
 *  questions. The flag is about a portal quirk (National only); the variant is
 *  just a field those insurers need on the proposal. Reliance is the odd one
 *  out here, not National. */
const hasVehicleVariant = (company) =>
  ["national", "kshema"].includes(String(company || "").trim().toLowerCase());

/** True when a selling price is driving the IDV range. */
const hasSellingPrice = (values) =>
  Number(String(values?.manufacturerSellingPrice ?? "").replace(/[^0-9.]/g, "")) > 0;

/** Who gets the Manufacturer's Selling Price -> IDV range calculator.
 *
 *  All three insurers. For National it feeds the portal's own "model not
 *  found" flow; for Reliance and KSHEMA it is purely a convenience — the
 *  derived range is a normal editable IDV range, exactly as if it had been
 *  typed by hand. */
const hasSellingPriceCalculator = (company) =>
  ["national", "reliance", "kshema"].includes(
    String(company || "").trim().toLowerCase()
  );

// One row = ONE insurer's vehicle. A policy sold by two insurers pairs two rows in
// Master > Combined Make and Model, so there is no OD/TP setup on this form.
// (camelCase on purpose: React Refresh treats a capitalised top-level `const X = call(...)` as a component
// factory and registers its callback as a component — this is plain data.)
const companyOptions = PROVIDER_COMPANIES.map((company) => ({ label: company, value: company }));

/** Pre-filled on a NEW record only, for every insurer. These are the values
 *  that hold for the overwhelming majority of vehicles, so typing them on each
 *  entry was pure repetition — they stay fully editable.
 *
 *  Edit mode never sees these: the load loop below overwrites every key from
 *  the saved record, so an existing row with a blank field stays blank rather
 *  than silently acquiring a default it was never saved with. */
const NEW_RECORD_DEFAULTS = {
  seatingCapacity: "2",
  discount: "50",
  nilDipPrice: "0.4",
  paCoverMinAmount: "422",
};

/** Fuel Type is master data an admin maintains, so the stored casing is
 *  whatever they typed — "Petrol", "PETROL", "petrol". The schema's
 *  `upercase: true` is a typo and never uppercased anything. So match on the
 *  name and default to the LIST'S OWN string; hardcoding one spelling would
 *  leave the dropdown blank the moment it disagreed. */
const DEFAULT_FUEL_TYPE = "petrol";
const findDefaultFuelType = (list) =>
  list?.find(
    (item) =>
      String(item?.fuelType || "").trim().toLowerCase() === DEFAULT_FUEL_TYPE
  )?.fuelType || "";

const AddMakeModel = (props) => {
  const {
    selectedData,
    formType,
    setOpenMakeModelDrawer,
    title,
    GetData,
  } = props;
  let formRef = useRef();

  const [fuelTypeList, setFuelTypeList] = React.useState([]);
  // Held locally as well as published module-wide, so the hint under the price
  // field can state the CURRENT rule instead of a sentence baked into the code.
  const [idvFormula, setIdvFormula] = React.useState(() => getActiveIdvFormula());
  // The resolved Petrol option, kept so "Save & Create New" can re-apply it
  // after resetForm() — see onSubmit.
  const defaultFuelTypeRef = useRef("");

  const isAdd = formType !== "edit";

  const initialValues = {
    make: "",
    model: "",
    idvRangeFrom: "",
    idvRangeTo: "",
    cc: "",
    company: "",
    seatingCapacity: isAdd ? NEW_RECORD_DEFAULTS.seatingCapacity : "",
    discount: isAdd ? NEW_RECORD_DEFAULTS.discount : "",
    // Filled once GetFuelType resolves — the default has to come from the
    // fetched list, not a literal, so it cannot be set here.
    fuelType: "",
    vehicleVariant: "",
    modelNotFound: false,
    manufacturerSellingPrice: "",
    // This vehicle's own depreciation. Seeded from the IDV_RANGE formula on a
    // new record once it loads; an existing record keeps whatever it was saved
    // with, including blank.
    depreciationPercent: "",
    // Per-vehicle Zero Depreciation value. Deliberately NOT required to save:
    // the Online Policy asks for it when it is missing and writes it back here.
    nilDipPrice: isAdd ? NEW_RECORD_DEFAULTS.nilDipPrice : "",
    // Whether the Online Policy offers TPPD Restrict for this vehicle.
    tppdRequired: false,
    // Per-vehicle MINIMUM PA Cover amount. Blank falls back to the default 422.
    paCoverMinAmount: isAdd ? NEW_RECORD_DEFAULTS.paCoverMinAmount : "",
  };

  const UserId = sessionStorage.getItem("UserId");

  useEffect(() => {
    GetFuelType().then((res) => {
      if (res?.data) {
        setFuelTypeList(res.data);
      }
    });

    // Published module-wide, so deriveIdvRange below (and the Online Policy's
    // own fallback) use the saved formula instead of the built-in default.
    GetFormula(FORMULA_NAMES.IDV_RANGE).then((res) => {
      if (!res?.data?.values) return;
      const before = getActiveIdvFormula();
      const next = setActiveIdvFormula(res.data.values);
      setIdvFormula(next);

      // A NEW record starts on the formula's depreciation; an existing one is
      // left exactly as it was saved.
      // Blank-checked, NOT falsy-checked: a deliberate 0 is a real value and
      // must not be overwritten with the formula's default.
      const current = formRef?.values?.depreciationPercent;
      if (isAdd && (current === "" || current === null || current === undefined)) {
        formRef?.setFieldValue?.(
          "depreciationPercent",
          String(next.depreciationPercent)
        );
      }

      // A price typed while this request was still in flight would have filled
      // the range from the FALLBACK 90/99 and then saved it, quietly ignoring
      // the admin's formula. Redo it — but only when what is on screen is
      // exactly that stale auto-fill, so a figure typed by hand is never
      // overwritten, and only on a NEW record: on an edit the range came from
      // the saved document and is not ours to change just by opening the form.
      if (!isAdd) return;
      const price = formRef?.values?.manufacturerSellingPrice;
      if (!price) return;

      const stale = deriveIdvRange(price, before);
      const fresh = deriveIdvRange(price, next);
      if (stale.from === fresh.from && stale.to === fresh.to) return;

      if (
        String(formRef.values.idvRangeFrom ?? "") === stale.from &&
        String(formRef.values.idvRangeTo ?? "") === stale.to
      ) {
        formRef.setFieldValue("idvRangeFrom", fresh.from);
        formRef.setFieldValue("idvRangeTo", fresh.to);
      }
    });

    if (formType === "edit") {
      Object.keys(initialValues).forEach((el) => {
        initialValues[el] = selectedData[el];
      });
      // The table shows "-" as a placeholder for empty variant — don't edit/save it
      if (initialValues.vehicleVariant === "-") {
        initialValues.vehicleVariant = "";
      }
    }
    formRef.setFieldValue(initialValues);
  }, []);

  // Apply the Petrol default once the master list arrives.
  //
  // A SEPARATE effect, not the GetFuelType callback: that callback closes over
  // the FIRST render's formRef, whose `values` are a stale snapshot, so the
  // "has the user already chosen?" guard there would always read blank. Here
  // formRef is the current render's bag, so the guard is real — an edit-mode
  // record's saved fuel type, or a choice the user made while the request was
  // still in flight, is never overwritten.
  useEffect(() => {
    if (!isAdd || !fuelTypeList.length) return;
    const petrol = findDefaultFuelType(fuelTypeList);
    defaultFuelTypeRef.current = petrol;
    if (petrol && !formRef?.values?.fuelType) {
      formRef.setFieldValue("fuelType", petrol);
    }
  }, [fuelTypeList]);

  const onKeyDownHandler = (e, values) => {
    if (e.key === "Enter") {
      e.preventDefault();
      onSubmit(e, values, "Save");
    }
  };

  const onSubmit = (e, data, keys) => {
    ["make", "model"].forEach(
      (item) => (data[item] = data[item]?.toUpperCase())
    );
    // Variant applies to National and KSHEMA; require it there, clear it
    // otherwise so a hidden field never saves a stale value.
    if (hasVehicleVariant(data.company)) {
      if (!data.vehicleVariant || data.vehicleVariant === "-") {
        ToastError(`Vehicle Variant is required for ${data.company} company!`);
        return;
      }
      data.vehicleVariant = data.vehicleVariant.toUpperCase();
    } else {
      data.vehicleVariant = "";
    }
    // A blank depreciation must stay unset so it falls back to the formula.
    // Left as "", Mongoose casts it to 0 — which is a real value meaning "no
    // depreciation at all", and the record would quietly price at full value.
    if (
      data.depreciationPercent === "" ||
      data.depreciationPercent === undefined
    ) {
      data.depreciationPercent = null;
    } else {
      data.depreciationPercent = Number(data.depreciationPercent);
    }

    data.updatedAt = new Date();

    // Which IDV source is required depends on the checkbox: with "model not
    // found" the selling price drives a derived range; without it the two range
    // fields are typed by hand. Requiring the ranges unconditionally (as before)
    // made a flagged record impossible to save.
    const idvSourceProvided = data?.modelNotFound
      ? !!data?.manufacturerSellingPrice
      : !!data?.idvRangeFrom && !!data?.idvRangeTo;

    if (e.key === "Enter" || keys === "Save") {
      if (
        data?.make &&
        data?.model &&
        idvSourceProvided &&
        data?.cc &&
        data?.company &&
        data?.seatingCapacity &&
        data?.fuelType &&
        data?.discount
      ) {
        if (formType === "add") {
          data.createdBy = UserId;
        } else if (formType === "edit") {
          data.updatedBy = UserId;
        }
        let SubmitType =
          formType === "edit"
            ? UpdateMakeModel(selectedData._id, data)
            : PostMakeModel(data);
        SubmitType.then((res) => {
          if (!res) {
            setOpenMakeModelDrawer(true);
          } else {
            setOpenMakeModelDrawer(false);
            GetData();
          }
        }).catch((error) => {
          console.log(error);
        });
      } else {
        ToastError("Fill all the fields!!");
      }
    } else if (keys === "Save&Create") {
      if (
        data?.make &&
        data?.model &&
        idvSourceProvided &&
        data?.cc &&
        data?.company &&
        data?.seatingCapacity &&
        data?.fuelType &&
        data?.discount
      ) {
        PostMakeModel(data).then((res) => {
          if (res) {
            // resetForm() restores initialValues, which carries the static
            // defaults — but not Fuel Type, which was only ever set after the
            // master list loaded. Re-apply it so the next entry starts from
            // the same place the first one did.
            formRef.resetForm();
            if (defaultFuelTypeRef.current) {
              formRef.setFieldValue("fuelType", defaultFuelTypeRef.current);
            }
          }
          GetData();
        });
      } else {
        ToastError("Fill all the fields!!");
      }
    }
  };

  return (
    <>
      <div className="DrawerContainer">
        <Grid container className="DrawerHeader">
          <Grid item xs={6} sm={6}>
            <Typography>{title} Make and Model</Typography>
          </Grid>
          <Grid item xs={6} sm={6} className="d-flex justify-content-end">
            <CloseIcon
              onClick={() => setOpenMakeModelDrawer(false)}
              sx={{ cursor: "pointer" }}
            />
          </Grid>
        </Grid>
        <Formik
          initialValues={initialValues}
          onSubmit={(values) => {
            onSubmit(values);
          }}
          innerRef={(ref) => {
            if (ref) {
              formRef = ref;
            }
          }}
        >
          {({ values, setFieldValue }) => (
            <Form onKeyDown={(e) => onKeyDownHandler(e, values)}>
              <div className={Styles.addContent}>
                <Grid container spacing={2}>
                  {/* ── Insurer ── */}
                  <Grid item xs={12}>
                    <Typography
                      sx={{
                        fontSize: "13px",
                        fontWeight: 600,
                        color: "#1E3A5F",
                        mt: 1,
                        pb: 0.5,
                        borderBottom: "1px solid #eaecf0",
                      }}
                    >
                      Insurer
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography
                      className={Styles.inputTitle}
                      sx={{ fontSize: "12px", mb: 1 }}
                    >
                      Company
                    </Typography>
                    <Field
                      name="company"
                      as="select"
                      className="InputFiled"
                      onChange={(e) => {
                        const nextCompany = e.target.value;
                        setFieldValue("company", nextCompany);
                        // Clear each hidden field on its OWN rule, not one
                        // shared "is it National?" test — the variant now
                        // survives a National -> KSHEMA switch, where before it
                        // would have been wiped even though the field is still
                        // on screen asking for a value.
                        if (!hasVehicleVariant(nextCompany)) {
                          setFieldValue("vehicleVariant", "");
                        }
                        if (!isNationalCompany(nextCompany)) {
                          setFieldValue("modelNotFound", false);
                        }
                        // The selling price is NOT cleared here: all three
                        // insurers use it for the IDV-range calculator, and
                        // wiping it would silently discard a figure the user
                        // can still see on screen.
                        // Only drop the price for a company that has no
                        // calculator at all (none today — future-proofing).
                        if (!hasSellingPriceCalculator(nextCompany)) {
                          setFieldValue("manufacturerSellingPrice", "");
                        }
                      }}
                    >
                      <option value="">Select Company</option>
                      {companyOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </Field>
                  </Grid>
                  {/* ── Vehicle Details ── */}
                  <Grid item xs={12}>
                    <Typography
                      sx={{
                        fontSize: "13px",
                        fontWeight: 600,
                        color: "#1E3A5F",
                        mt: 1,
                        pb: 0.5,
                        borderBottom: "1px solid #eaecf0",
                      }}
                    >
                      Vehicle Details
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography
                      className={Styles.inputTitle}
                      sx={{ fontSize: "12px", mb: 1 }}
                    >
                      Make
                    </Typography>
                    <Field
                      name="make"
                      placeHolder="Enter Make"
                      className="InputFiled"
                      style={{ textTransform: "uppercase", }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography
                      className={Styles.inputTitle}
                      sx={{ fontSize: "12px", mb: 1 }}
                    >
                      Model
                    </Typography>
                    <Field
                      name="model"
                      className="InputFiled"
                      placeHolder="Enter Model"
                      style={{ textTransform: "uppercase", }}
                    />
                  </Grid>
                  {hasVehicleVariant(values.company) && (
                    <Grid item xs={12} sm={6}>
                      <Typography
                        className={Styles.inputTitle}
                        sx={{ fontSize: "12px", mb: 1 }}
                      >
                        Vehicle Variant *
                      </Typography>
                      <Field
                        name="vehicleVariant"
                        className="InputFiled"
                        placeHolder="Enter Vehicle Variant"
                        style={{ textTransform: "uppercase", }}
                      />
                    </Grid>
                  )}
                  <Grid item xs={12} sm={6}>
                    <Typography
                      className={Styles.inputTitle}
                      sx={{ fontSize: "12px", mb: 1 }}
                    >
                      CC
                    </Typography>
                    <Field
                      name="cc"
                      className="InputFiled"
                      placeHolder="Enter CC"
                      style={{ textTransform: "uppercase", }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography
                      className={Styles.inputTitle}
                      sx={{ fontSize: "12px", mb: 1 }}
                    >
                      Fuel Type
                    </Typography>
                    <Field name="fuelType" as="select" className="InputFiled">
                      <option value="">Select Fuel Type</option>
                      {fuelTypeList.map((item) => (
                        <option key={item._id} value={item.fuelType}>
                          {item.fuelType}
                        </option>
                      ))}
                    </Field>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography
                      className={Styles.inputTitle}
                      sx={{ fontSize: "12px", mb: 1 }}
                    >
                      Seating Capacity
                    </Typography>
                    <Field
                      name="seatingCapacity"
                      className="InputFiled"
                      placeHolder="Enter Seating Capacity"
                      style={{ textTransform: "uppercase", }}
                    />
                  </Grid>
                  {/* ── IDV Details ── */}
                  <Grid item xs={12}>
                    <Typography
                      sx={{
                        fontSize: "13px",
                        fontWeight: 600,
                        color: "#1E3A5F",
                        mt: 1,
                        pb: 0.5,
                        borderBottom: "1px solid #eaecf0",
                      }}
                    >
                      IDV Details
                    </Typography>
                  </Grid>
                  {isNationalCompany(values.company) && (
                  <Grid item xs={12} sm={12}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={!!values.modelNotFound}
                          onChange={(e) => {
                            const isChecked = e.target.checked;
                            setFieldValue("modelNotFound", isChecked);
                            if (isChecked) {
                              // Selling price becomes authoritative — drop any
                              // hand-typed range and derive it instead.
                              const { from, to } = deriveIdvRange(
                                values.manufacturerSellingPrice,
                                undefined,
                                values.depreciationPercent
                              );
                              setFieldValue("idvRangeFrom", from);
                              setFieldValue("idvRangeTo", to);
                            } else {
                              // Back to manual ranges — clear the price so it
                              // cannot be saved as if it were still in use.
                              setFieldValue("manufacturerSellingPrice", "");
                              setFieldValue("idvRangeFrom", "");
                              setFieldValue("idvRangeTo", "");
                            }
                          }}
                        />
                      }
                      label="Not able to Find the required Model & Variant"
                    />
                  </Grid>
                  )}
                  {hasSellingPriceCalculator(values.company) && (
                  <Grid item xs={12} sm={6}>
                    <Typography
                      className={Styles.inputTitle}
                      sx={{ fontSize: "12px", mb: 1 }}
                    >
                      Manufacturer's Selling Price
                      {values.modelNotFound ? " *" : ""}
                    </Typography>
                    <Field
                      type="text"
                      name="manufacturerSellingPrice"
                      className="InputFiled"
                      placeHolder="Enter Manufacturer's Selling Price"
                      // Gated by the checkbox ONLY on National, where the price
                      // replaces the IDV. On Reliance it is always available:
                      // there is no checkbox, it just fills the range below.
                      disabled={
                        isNationalCompany(values.company) && !values.modelNotFound
                      }
                      // digitsOnlyChange keeps the caret where it was: writing
                      // a filtered value back to a controlled input otherwise
                      // snaps the cursor to the end on every keystroke, which
                      // makes editing the FIRST digit impossible.
                      onChange={digitsOnlyChange(
                        setFieldValue,
                        "manufacturerSellingPrice",
                        (numericValue) => {
                          // Keep the derived range in step as the price is
                          // typed. Clearing the price clears the range too,
                          // rather than stranding a figure derived from a
                          // price that is gone.
                          const { from, to } = deriveIdvRange(
                            numericValue,
                            undefined,
                            values.depreciationPercent
                          );
                          setFieldValue("idvRangeFrom", from);
                          setFieldValue("idvRangeTo", to);
                        }
                      )}
                    />
                    <Typography sx={{ fontSize: "11px", color: "#667085", mt: 0.5 }}>
                      {isNationalCompany(values.company) && !values.modelNotFound
                        ? "Tick the checkbox above to use this."
                        : `IDV = ${describeDepreciation(
                            values.depreciationPercent
                          )}. Range = ${describeIdvSide(
                            idvFormula.minPercent
                          )} to ${describeIdvSide(idvFormula.maxPercent)}.`}
                    </Typography>
                  </Grid>
                  )}
                  {/* ── Depreciation, the IDV it produces, and the band ──
                      IDV is derived, not typed: it is the selling price less
                      this vehicle's depreciation. The range either side comes
                      from the saved IDV_RANGE formula. */}
                  {hasSellingPriceCalculator(values.company) && (
                    <Grid item xs={12} sm={6}>
                      <Typography
                        className={Styles.inputTitle}
                        sx={{ fontSize: "12px", mb: 1 }}
                      >
                        Depreciation (%)
                      </Typography>
                      <Field
                        type="text"
                        name="depreciationPercent"
                        className="InputFiled"
                        placeHolder={`Default ${idvFormula.depreciationPercent}`}
                        disabled={
                          isNationalCompany(values.company) &&
                          !values.modelNotFound
                        }
                        onChange={(e) => {
                          const raw = (e.target.value || "").replace(
                            /[^0-9.]/g,
                            ""
                          );
                          // 100% would value the vehicle at nothing.
                          const capped =
                            raw !== "" && Number(raw) >= 100 ? "99" : raw;
                          setFieldValue("depreciationPercent", capped);
                          const { from, to } = deriveIdvRange(
                            values.manufacturerSellingPrice,
                            undefined,
                            capped
                          );
                          setFieldValue("idvRangeFrom", from);
                          setFieldValue("idvRangeTo", to);
                        }}
                      />
                      <Typography
                        sx={{ fontSize: "11px", color: "#667085", mt: 0.5 }}
                      >
                        Enter 0 for no depreciation. Leave blank to use the
                        formula's {idvFormula.depreciationPercent}%.
                      </Typography>
                    </Grid>
                  )}

                  {hasSellingPriceCalculator(values.company) &&
                    !!calculateIdv(
                      values.manufacturerSellingPrice,
                      values.depreciationPercent
                    ) && (
                      <Grid item xs={12}>
                        <Box
                          sx={{
                            p: 2,
                            borderRadius: "8px",
                            border: "1px solid #EAECF0",
                            backgroundColor: "#F8F9FA",
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "baseline",
                              gap: 1,
                              flexWrap: "wrap",
                            }}
                          >
                            <Typography
                              sx={{ fontSize: "12px", color: "#667085" }}
                            >
                              IDV
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: "20px",
                                fontWeight: 700,
                                color: "#1E3A5F",
                              }}
                            >
                              ₹{" "}
                              {Number(
                                calculateIdv(
                                  values.manufacturerSellingPrice,
                                  values.depreciationPercent
                                )
                              ).toLocaleString("en-IN")}
                            </Typography>
                            <Typography
                              sx={{ fontSize: "11px", color: "#0288d1" }}
                            >
                              Depreciation Value (%):{" "}
                              {values.depreciationPercent === "" ||
                              values.depreciationPercent === undefined
                                ? idvFormula.depreciationPercent
                                : values.depreciationPercent}
                            </Typography>
                          </Box>

                          {/* The band the policy may quote within. Dragging an
                              end writes straight to the saved range fields, so
                              the slider and the two numbers below never
                              disagree. */}
                          {(() => {
                            const full = deriveIdvRange(
                              values.manufacturerSellingPrice,
                              undefined,
                              values.depreciationPercent
                            );
                            const lo = Number(full.from) || 0;
                            const hi = Number(full.to) || 0;
                            if (!hi || hi <= lo) return null;
                            // Clamped: a hand-typed figure outside the band
                            // must not throw the slider out of its own bounds.
                            const clamp = (v, fallback) => {
                              const n = Number(v);
                              if (!Number.isFinite(n)) return fallback;
                              return Math.min(Math.max(n, lo), hi);
                            };
                            const value = [
                              clamp(values.idvRangeFrom, lo),
                              clamp(values.idvRangeTo, hi),
                            ];
                            return (
                              <Box sx={{ mt: 1.5, px: 1 }}>
                                <Box
                                  sx={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                  }}
                                >
                                  <Typography
                                    sx={{ fontSize: "11px", color: "#667085" }}
                                  >
                                    Min ₹ {value[0].toLocaleString("en-IN")}
                                  </Typography>
                                  <Typography
                                    sx={{ fontSize: "11px", color: "#667085" }}
                                  >
                                    Max ₹ {value[1].toLocaleString("en-IN")}
                                  </Typography>
                                </Box>
                                <Slider
                                  value={value}
                                  min={lo}
                                  max={hi}
                                  step={1}
                                  disableSwap
                                  onChange={(_e, next) => {
                                    setFieldValue(
                                      "idvRangeFrom",
                                      String(next[0])
                                    );
                                    setFieldValue("idvRangeTo", String(next[1]));
                                  }}
                                  valueLabelDisplay="auto"
                                  valueLabelFormat={(v) =>
                                    `₹ ${v.toLocaleString("en-IN")}`
                                  }
                                  sx={{ color: "#1E3A5F", mt: -0.5 }}
                                />
                              </Box>
                            );
                          })()}
                        </Box>
                      </Grid>
                    )}

                  <Grid item xs={12} sm={6}>
                    <Typography
                      className={Styles.inputTitle}
                      sx={{ fontSize: "12px", mb: 1 }}
                    >
                      IDV Range From{values.modelNotFound ? "" : " *"}
                    </Typography>
                    <Field
                      type="text"
                      name="idvRangeFrom"
                      className="InputFiled"
                      placeHolder="Enter IDV Range From"
                      // Never disabled. The selling price above FILLS these at
                      // 90%-99%, but the figures stay editable at all times —
                      // the stored range is what the Online Policy uses, so
                      // locking it would make a deliberate adjustment
                      // impossible.
                      onChange={digitsOnlyChange(setFieldValue, "idvRangeFrom")}
                      style={{ textTransform: "uppercase", }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography
                      className={Styles.inputTitle}
                      sx={{ fontSize: "12px", mb: 1 }}
                    >
                      IDV Range To{values.modelNotFound ? "" : " *"}
                    </Typography>
                    <Field
                      type="text"
                      name="idvRangeTo"
                      className="InputFiled"
                      placeHolder="Enter IDV Range To"
                      // Never disabled. The selling price above FILLS these at
                      // 90%-99%, but the figures stay editable at all times —
                      // the stored range is what the Online Policy uses, so
                      // locking it would make a deliberate adjustment
                      // impossible.
                      onChange={digitsOnlyChange(setFieldValue, "idvRangeTo")}
                      style={{ textTransform: "uppercase", }}
                    />
                  </Grid>
                  {/* ── Online Policy Defaults ──
                      Values that pre-fill the Online Policy form. Kept apart
                      from IDV Details, which had grown into a mixed bag of IDV
                      settings and unrelated policy values. */}
                  <Grid item xs={12}>
                    <Typography
                      sx={{
                        fontSize: "13px",
                        fontWeight: 600,
                        color: "#1E3A5F",
                        mt: 1,
                        pb: 0.5,
                        borderBottom: "1px solid #eaecf0",
                      }}
                    >
                      Online Policy Defaults
                    </Typography>
                    <Typography sx={{ fontSize: "11px", color: "#667085", mt: 0.5 }}>
                      Used to fill the Online Policy automatically. Leave a value
                      blank and the policy form will ask for it, then save it back
                      here.
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography
                      className={Styles.inputTitle}
                      sx={{ fontSize: "12px", mb: 1 }}
                    >
                      Discount (%)
                    </Typography>
                    <Field
                      type="text"
                      name="discount"
                      className="InputFiled"
                      placeHolder="Enter Discount % (Max 100)"
                      onChange={(e) => {
                        const inputValue = e.target.value || "";
                        // Only allow numbers with a single optional decimal point
                        let numericValue = inputValue.replace(/[^0-9.]/g, "");
                        const parts = numericValue.split(".");
                        if (parts.length > 2) {
                          numericValue = parts[0] + "." + parts.slice(1).join("");
                        }
                        // Percentage cannot exceed 100
                        if (numericValue !== "" && Number(numericValue) > 100) {
                          numericValue = "100";
                        }
                        setFieldValue("discount", numericValue);
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography
                      className={Styles.inputTitle}
                      sx={{ fontSize: "12px", mb: 1 }}
                    >
                      Nil Dip Price
                    </Typography>
                    <Field
                      type="number"
                      name="nilDipPrice"
                      className="InputFiled"
                      placeHolder="Enter Nil Dip Price"
                      // onChange={digitsOnlyChange(setFieldValue, "nilDipPrice")}
                       onChange={(e) => {
                        const inputValue = e.target.value || "";
                        let numericValue = inputValue.replace(/[^0-9.]/g, "")
                        setFieldValue("nilDipPrice", numericValue);
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography
                      className={Styles.inputTitle}
                      sx={{ fontSize: "12px", mb: 1 }}
                    >
                      Minimum PA Cover Amount
                    </Typography>
                    <Field
                      type="text"
                      name="paCoverMinAmount"
                      className="InputFiled"
                      placeHolder="Default 422"
                      onChange={digitsOnlyChange(setFieldValue, "paCoverMinAmount")}
                    />
                    <Typography sx={{ fontSize: "11px", color: "#667085", mt: 0.5 }}>
                      Lowest PA Cover amount allowed when an insurer sells the
                      PA for this vehicle. Blank means 422. Brisk is always 177.
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={!!values.tppdRequired}
                          onChange={(e) =>
                            setFieldValue("tppdRequired", e.target.checked)
                          }
                          color="primary"
                        />
                      }
                      label="TPPD Restrict wanted for this vehicle"
                    />
                    <Typography sx={{ fontSize: "11px", color: "#667085" }}>
                      When off, the Online Policy hides the TPPD Restrict option
                      and never applies it.
                    </Typography>
                  </Grid>
                </Grid>
                
              </div>
              <div className="Dialog_Footers">
                <div className="Dialog_Button_Groups">
                  <Button
                    onClick={() => setOpenMakeModelDrawer(false)}
                    className="Dialog_Cancels"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    onClick={(e) => onSubmit(e, values, "Save")}
                    className="Dialog_Saves"
                  >
                    {selectedData?._id ? "Update" : "Save & Exit"}
                  </Button>
                </div>
                <Button
                  sx={{
                    display: formType === "add" ? "block" : "none",
                    marginLeft: "10px",
                  }}
                  className="Dialog_Save_Exits"
                  onClick={(e) => onSubmit(e, values, "Save&Create")}
                >
                  Save & Create New
                </Button>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </>
  );
};

export default AddMakeModel;

